"use client";

import type React from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  validateOtp,
  resendOtp,
  selectAuthError,
  selectAuthLoading,
} from "@/store/slices/authSlice";
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { RootState } from "@/store";

export default function OtpForm() {
  const [otp, setOtp] = useState(["", "", "", ""]); // 4-digit OTP
  const [timeLeft, setTimeLeft] = useState(30); //30 seconds
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const dispatch = useDispatch<ThunkDispatch<RootState, unknown, AnyAction>>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get auth state from Redux
  const isLoading = useSelector(selectAuthLoading);
  const error = useSelector(selectAuthError);

  // Extract email and id from URL if present (for OTP verification)
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email") || "your email";
  const token = queryParams.get("token") || "";

  // Timer countdown
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    // No need to clear error state as we're using Redux for error management
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    const newOtp = [...otp];

    for (let i = 0; i < pastedData.length && i < 6; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }

    setOtp(newOtp);

    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
    const focusIndex = nextEmptyIndex === -1 ? 3 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const otpString = otp.join("");
    if (otpString.length !== 4) {
      // Don't use setError directly as we're using Redux for error state
      return;
    }

    if (!token) {
      console.error("Missing ID parameter in URL");
      return;
    }

    try {
      const resultAction = await dispatch(
        validateOtp({ id: token, otp: otpString }) as unknown as AnyAction
      );

      if (validateOtp.fulfilled.match(resultAction)) {
        const validationData = resultAction.payload;

        // Check if validationData has the id property
        if (validationData && "id" in validationData) {
          const tokenId = validationData.id as string;
          // Navigate to password generation page with token
          navigate(`/auth/generate-password?token=${tokenId}`);
        }
      }
    } catch (error) {
      console.error("OTP validation error:", error);
    }
  };

  const handleResend = async () => {
    if (!token) {
      console.error("Missing ID parameter in URL");
      return;
    }

    try {
      const resultAction = await dispatch(
        resendOtp(token) as unknown as AnyAction
      );

      if (resendOtp.fulfilled.match(resultAction)) {
        const responseData = resultAction.payload;

        if (responseData && "id" in responseData) {
          const newToken = responseData.id as string;

          // For hash router, we need to update the navigation state
          // instead of directly modifying the URL
          navigate(`/auth/otp?email=${email}&token=${newToken}`, {
            replace: true,
          });
        }

        // Reset timer
        setTimeLeft(30);
        setCanResend(false);
        setOtp(["", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("Failed to resend OTP:", error);
    }
  };

  return (
    <div className="bg-white rounded-3xl p-[18px] w-full max-w-md shadow-2xl">
      <div className="mb-[2.5px] relative z-50">
        <button
          type="button"
          onClick={() => navigate("/auth/signin?type=signin")}
          className="text-[#1E1E1E] text-[14px] font-[500] no-underline hover:underline cursor-pointer flex items-center before:text-lg"
        >
          <ChevronLeft />
          Go back
        </button>
      </div>

      <div className="space-y-6 pb-[40px] p-[10px]">
        <div className="flex items-center justify-center">
          {/* <Link href="/auth/forgot-password" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link> */}
          <div className="text-center">
            <h3 className="text-xl mt-2 font-bold text-center text-gray-900">
              OTP Verification
            </h3>
            <p className="text-gray-600 font-normal px-3 mt-3 text-sm">
              We will send you a one time password on the email address{" "}
              <strong>({email})</strong>
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="border border-[#FF3B30]">
            <AlertCircle className="h-4 w-4 text-[#FF3B30]" />
            <AlertDescription className="text-[#FF3B30]">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <div className="flex px-5 mb-4 justify-between ">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    if (el) {
                      inputRefs.current[index] = el;
                    }
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:ring-[1px] focus:ring-[#ED776B] focus:border-[#ED776B] outline-none transition-all"
                />
              ))}
            </div>
          </div>

          <div className=" mt-2">
            <div className="text-center mb-3">
              {timeLeft > 0 && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium text-[21.7px] text-[#ff6e64]">
                    {formatTime(timeLeft)}
                  </span>
                </p>
              )}
            </div>

            <div className="text-center flex justify-center items-center">
              <p className="text-[18px] font-[400] text-gray-600">
                Didn't receive OTP
              </p>
              {canResend ? (
                <Button
                  onClick={handleResend}
                  variant="ghost"
                  className="text-[18px] text-[#ED776B] hover:text-[#ED776B] hover:bg-transparent font-medium"
                  disabled={isLoading}
                >
                  resend OTP
                </Button>
              ) : (
                <p className="text-sm ml-2 text-gray-400">
                  Resend available in {formatTime(timeLeft)}
                </p>
              )}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full uppercase bg-[#ff6e64] hover:bg-[#ff5a4f] text-white font-medium py-3 rounded-lg"
            disabled={isLoading || otp.join("").length !== 4}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
